import React from "react";
import styled from "@emotion/styled";
import { NavLink } from "react-router-dom";
import { Helmet } from "react-helmet-async";

import {
  Box,
  Breadcrumbs as MuiBreadcrumbs,
  Divider as MuiDivider,
  Grid,
  Link,
  Typography as MuiTypography,
  List,
  ListItem,
} from "@mui/material";
import { spacing } from "@mui/system";

const Divider = styled(MuiDivider)(spacing);

const Breadcrumbs = styled(MuiBreadcrumbs)(spacing);

const Typography = styled(MuiTypography)(spacing);

function PoliciesAndProcedures() {
  return (
    <React.Fragment>
      <Helmet title="Policies and Procedures" />

      <Grid container spacing={6} justifyContent="center">
        <Grid item xs={12} lg={9} xl={7}>
          <Typography variant="h2" gutterBottom display="inline">
            Notice of Privacy Practices
          </Typography>

          <Breadcrumbs aria-label="Breadcrumb" mt={2}>
            <Link component={NavLink} to="/">
              Dashboard
            </Link>
            <Link component={NavLink} to="/documentation/policies-and-procedures">
              Policies And Procedures
            </Link>
            <Typography>Notice of Privacy Practices</Typography>
          </Breadcrumbs>

          <Divider my={6} />

          <Box display="flex" flexDirection="column" sx={{ mb: 2 }} style={{ textTransform: "uppercase" }}>
            <Typography variant="body1" gutterBottom>
              <strong>
                This Notice Describes How Medical Information About You May Be Used And Disclosed And How You Can Get
                Access To This Information.
              </strong>
            </Typography>
          </Box>

          <Box display="flex" flexDirection="column" sx={{ mb: 5 }} style={{ textTransform: "uppercase" }}>
            <Typography variant="body1" gutterBottom>
              <strong>Please Review It Carefully.</strong>
            </Typography>
          </Box>

          <Box display="flex" flexDirection="column" sx={{ mb: 5 }}>
            <Typography variant="body1" gutterBottom>
              <strong>Effective Date:</strong> January 4, 2024
            </Typography>
          </Box>

          <Typography variant="h6" gutterBottom>
            Our Commitment to Your Privacy
          </Typography>
          <Typography variant="body1" paragraph>
            OnRounds AI is dedicated to maintaining the privacy of your personal health information (PHI) as part of
            providing professional care. We are required by law to keep your information private, provide you with this
            notice, and follow the practices outlined in it.
          </Typography>
          <Typography variant="h6" gutterBottom>
            Use and Disclosure of PHI
          </Typography>
          <Typography variant="body1">We may use and disclose your PHI for the following purposes:</Typography>
          <List>
            <ListItem>
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>For Treatment:</b> Your PHI can be used to provide you with medical treatment or services.
                </Typography>
              </Box>
            </ListItem>
            <ListItem>
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>For Payment:</b> Your PHI can be used and disclosed to bill and collect payment for the services
                  provided to you.
                </Typography>
              </Box>
            </ListItem>
            <ListItem>
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>For Health Care Operation:</b> We may use and disclose your PHI for operational purposes, including
                  quality assessment, training, licensing, and accreditation activities.
                </Typography>
              </Box>
            </ListItem>
            <ListItem>
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Appointment Reminders:</b> Your PHI may be used to remind you of upcoming appointments.
                </Typography>
              </Box>
            </ListItem>
            <ListItem>
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Treatment Alternatives and Health-Related Benefits:</b> We may use and disclose your PHI to inform
                  you about treatment options or health-related benefits and services that may be of interest to you.
                </Typography>
              </Box>
            </ListItem>
            <ListItem>
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Others Involved in Your Healthcare:</b> Unless you object, we may disclose to a family member, a
                  relative, a close friend, or any other person you identify, your PHI that directly relates to that
                  person's involvement in your health care.
                </Typography>
              </Box>
            </ListItem>
          </List>
          <Typography variant="h6" gutterBottom>
            Your Rights Regarding Your PHI
          </Typography>
          <Typography variant="body1">You have the following rights concerning your PHI:</Typography>
          <List>
            <ListItem>
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Right to Inspect and Copy:</b> You have the right to inspect and copy PHI that may be used to make
                  decisions about your care.
                </Typography>
              </Box>
            </ListItem>
            <ListItem>
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Right to Amend:</b> If you feel that the PHI we have is incorrect or incomplete, you may ask us to
                  amend the information.
                </Typography>
              </Box>
            </ListItem>
            <ListItem>
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Right to an Accounting of Disclosures:</b> You have the right to request an accounting of certain
                  disclosures we have made of your PHI.
                </Typography>
              </Box>
            </ListItem>
            <ListItem>
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Right to Request Restrictions:</b> You have the right to request a restriction or limitation on the
                  PHI we use or disclose for treatment, payment, or health care operations.
                </Typography>
              </Box>
            </ListItem>
            <ListItem>
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Right to Request Confidential Communication:</b> You have the right to request that we communicate
                  with you about medical matters in a certain way or at a certain location.
                </Typography>
              </Box>
            </ListItem>
            <ListItem>
              <Box display="flex" flexDirection="column">
                <Typography component="span">
                  <b>Right to a Paper Copy of This Notice:</b> You have the right to a paper copy of this notice at any
                  time.
                </Typography>
              </Box>
            </ListItem>
          </List>
          <Typography variant="h6" gutterBottom>
            Changes to This Notice
          </Typography>
          <Typography variant="body1" paragraph>
            We reserve the right to change our privacy practices and this notice. We will make the new notice available
            upon request and on our website.
          </Typography>
          <Typography variant="h6" gutterBottom>
            Complaints
          </Typography>
          <Typography variant="body1" paragraph>
            If you believe your privacy rights have been violated, you may file a complaint with us or with the
            Secretary of the Department of Health and Human Services.
          </Typography>
          <Typography variant="h6" gutterBottom>
            Contact Information
          </Typography>
          <Typography variant="body1" paragraph>
            For more information about our privacy practices or to file a complaint, please contact:
          </Typography>
          <Typography variant="body1" paragraph>
            Norman Nolasco [ nnolasco@onrounds.com ]
          </Typography>
        </Grid>
      </Grid>
    </React.Fragment>
  );
}

export default PoliciesAndProcedures;
